import { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams, Link } from "react-router";
import { toast } from "sonner";
import { paths } from "@shared-core/routing/routes";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  Catalogue,
  CatalogueEditability,
  CatalogueField,
  CatalogueItem,
  CatalogueRecord,
  Narrowing,
  QuantityBreak,
} from "@shared-core/data/catalogue-types";
import type { PriceGroupSummary } from "@shared-core/data/api/pricing";
import type * as API from "@shared-core/data/api-types";
import { formatDateOrDash } from "@shared-core/domain/formatters";
import { useEstRequestSchema } from "@shared-core/domain/useEstRequestSchema";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import { useChangeVersion } from "@shared-core/domain/object-ui/hooks";
import { useObjectManifest } from "@shared-core/domain/object-ui/manifest";
import { useCommandHost } from "../app/useCommandHost";
import { Button } from "../app/components/primitives/button";
import { Badge } from "../app/components/primitives/badge";
import { Input } from "../app/components/primitives/input";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../app/components/primitives/card";
import { EditorHeader } from "../app/components/patterns/EditorHeader";
import { UnsavedChangesGuard } from "../app/components/patterns/UnsavedChangesGuard";
import { CatalogueItemGrid } from "../app/components/domain/catalogue/CatalogueItemGrid";
import { CatalogueItemCreator } from "../app/components/domain/catalogue/CatalogueItemCreator";
import { NarrowingEditor } from "../app/components/domain/catalogue/NarrowingEditor";
import { ItemMerchandisingEditor } from "../app/components/domain/catalogue/ItemMerchandisingEditor";
import { ItemArtworkPanel } from "../app/components/domain/catalogue/ItemArtworkPanel";
import { ItemPhysicalFactsPanel } from "../app/components/domain/catalogue/ItemPhysicalFactsPanel";
import {
  RouteNarrowingEditor,
  type RouteChoice,
} from "../app/components/domain/catalogue/RouteNarrowingEditor";
import type { RouteNarrowing } from "@shared-core/data/supplier-pricing-types";
import type { SpeedLadderRung } from "@shared-core/data/speed-types";
import { IN_HOUSE_ROUTE } from "@shared-core/data/supplier-pricing-types";
import {
  describeRouteNarrowing,
  isEveryRoute,
} from "@shared-core/domain/supplier-pricing-presentation";
import {
  extractErrorDetailLines,
  extractFieldErrors,
  type FieldError,
} from "@shared-core/data/validation";
import {
  fieldErrorMessages,
  inheritedCopyFrom,
  presentationInputFrom,
  type InheritedCopy,
} from "@shared-core/domain/catalogue-merchandising";
import { mutationErrorMessage } from "@shared-core/domain/mutation-error-message";
import {
  ensureCatalogueDraft,
  useCatalogueDraft,
  removeCatalogueItem,
  upsertCatalogueItem,
  revertCatalogueDraft,
  hydrateCatalogueItem,
} from "../app/components/domain/catalogue/catalogue-draft";
import {
  Upload,
  Download,
  ArrowLeft,
  ArrowUpRight,
  Loader2,
} from "lucide-react";

// Catalogue editor — one page with an invariant header. The body swaps between
// the item grid, an inline item editor, and the new-item form. All item CRUD
// flows into the working copy; Save commits it via the management API.
//
// Which item is open lives in the URL (`?item=<id>`), not component state —
// that is what makes the editor's edit view addressable/shareable (R6). It is
// a query param on the *same* route rather than a nested route: opening an
// item must not change `pathname`, or the page remounts on every grid<->item
// hop (refetching the catalogue) and UnsavedChangesGuard's predicate would
// need widening to allow the hop. A search-param-only navigation already
// fails that guard's own `nextLocation.pathname !== currentLocation.pathname`
// check, so the coupling dissolves rather than needing a patch — see
// `paths.settingsCatalogueItemEdit`.
//
// Whether that item's real config is safe to render is a separate question,
// answered by the draft's `hydratedItemIds` ledger (catalogue-draft.ts) — not
// cached locally. A grid click and a page load/refresh landing on the same
// URL must reach the same state, and deriving readiness fresh on every render
// is what keeps that true after a revert clears the ledger and the URL has
// not changed: the id briefly leaves the ledger, the effect below notices and
// re-fetches, and nothing needs to "notice" that a cached status went stale.
type View = { kind: "grid" } | { kind: "new" };

type HydrationFailure = {
  itemId: string;
  reason:
    "not_found" | "wrong_catalogue" | "fetch_failed" | "unsaved_item_lost";
};

function hydrationFailureMessage(reason: HydrationFailure["reason"]): string {
  switch (reason) {
    case "not_found":
      return "This item no longer exists in this catalogue.";
    case "wrong_catalogue":
      return "This item belongs to a different catalogue.";
    case "unsaved_item_lost":
      return "This item was never saved, and the unsaved draft was lost — most likely by a page refresh. There is nothing to recover.";
    case "fetch_failed":
      return "This item's saved configuration didn't load.";
  }
}

export function CatalogueEditorPage() {
  const { catalogueId } = useParams<{ catalogueId: string }>();
  const adapter = useDataAdapter();
  const [catalogue, setCatalogue] = useState<Catalogue | null>(null);
  const [record, setRecord] = useState<CatalogueRecord | null>(null);
  const [liveItems, setLiveItems] = useState<CatalogueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  // A command run on this page (Hand over to One…) reads the catalogue and its ownership again.
  const changeVersion = useChangeVersion();

  useEffect(() => {
    if (!catalogueId) return;
    let cancelled = false;
    setLoadError(null);
    Promise.all([
      adapter.getCatalogueWithItems(catalogueId),
      adapter.getCatalogueRecord(catalogueId),
    ])
      .then(([view, nextRecord]) => {
        if (cancelled) return;
        if (!view) {
          setLoadError("Catalogue not found");
          return;
        }
        setCatalogue(view.catalogue);
        setLiveItems(view.items);
        setRecord(nextRecord);
      })
      .catch(() => {
        if (!cancelled) setLoadError("Failed to load catalogue");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, catalogueId, changeVersion]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-muted-foreground">
        <Loader2 className="size-5 animate-spin" />
      </div>
    );
  }
  if (loadError || !catalogue || !record || !catalogueId) {
    return (
      <div className="py-10 text-center text-destructive text-sm">
        {loadError ?? "Catalogue not found"}
      </div>
    );
  }

  return (
    <CatalogueEditor
      key={catalogue.id}
      catalogue={catalogue}
      record={record}
      liveItems={liveItems}
      onLiveItemsChange={setLiveItems}
    />
  );
}

function CatalogueEditor({
  catalogue,
  record,
  liveItems,
  onLiveItemsChange,
}: {
  catalogue: Catalogue;
  record: CatalogueRecord;
  liveItems: CatalogueItem[];
  onLiveItemsChange: (items: CatalogueItem[]) => void;
}) {
  const { editability } = record;
  const index = useObjectManifest();
  const { openCommand, shell: commandShell } = useCommandHost();
  ensureCatalogueDraft(catalogue.id, liveItems);
  const { items, dirty, newItemIds, modifiedItemIds, hydratedItemIds } =
    useCatalogueDraft(catalogue.id);
  const itemsWithoutVatTreatment = items.filter(
    (item) => item.vatTreatment == null,
  ).length;
  const [view, setView] = useState<View>({ kind: "grid" });
  const [saving, setSaving] = useState(false);
  const capabilities = usePlatformCapabilities();
  const adapter = useDataAdapter();
  const [searchParams, setSearchParams] = useSearchParams();
  const editingItemId = searchParams.get("item");
  const [hydrationFailure, setHydrationFailure] =
    useState<HydrationFailure | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);
  // The 400 field errors from the last save, with the item they belong to
  // (their paths are relative to that item's request body).
  const [saveFailure, setSaveFailure] = useState<{
    itemId: string;
    errors: FieldError[];
  } | null>(null);

  const openItem = (itemId: string) => {
    setSearchParams((p) => {
      const next = new URLSearchParams(p);
      next.set("item", itemId);
      return next;
    });
  };
  const closeItem = () => {
    setSearchParams((p) => {
      const next = new URLSearchParams(p);
      next.delete("item");
      return next;
    });
  };

  // Hydration used to run inside the grid's click handler, which could simply
  // refuse to open the editor on failure. A URL is not a transition anyone can
  // refuse — the route matches and the component renders regardless — so the
  // same "do not show an editable panel over unresolved config" guarantee is
  // re-expressed below as a render precondition (isReady), and this effect's
  // only job is to make that precondition become true.
  //
  // Keyed on hydratedItemIds (not just the id): a revert clears the ledger
  // without changing the URL, and re-running on that change — rather than
  // caching "ready" once — is what makes the item re-fetch instead of the
  // panel hanging on stale local state. See the file-level comment on `View`.
  useEffect(() => {
    if (!editingItemId) return;
    if (
      hydratedItemIds.has(editingItemId) ||
      newItemIds.has(editingItemId) ||
      modifiedItemIds.has(editingItemId)
    ) {
      setHydrationFailure(null);
      return;
    }
    if (editingItemId.startsWith("new-")) {
      // An unsaved item's id has no server identity. This is the ordinary
      // outcome of refreshing on a just-created item (the in-memory draft is
      // gone) — calling the API for it would 404 in a way that misdescribes
      // what actually happened.
      setHydrationFailure({
        itemId: editingItemId,
        reason: "unsaved_item_lost",
      });
      return;
    }
    let cancelled = false;
    adapter
      .getResolvedCatalogueItem(editingItemId)
      .then((resolved) => {
        if (cancelled) return;
        if (!resolved) {
          setHydrationFailure({ itemId: editingItemId, reason: "not_found" });
          return;
        }
        if (resolved.item.catalogueId !== catalogue.id) {
          setHydrationFailure({
            itemId: editingItemId,
            reason: "wrong_catalogue",
          });
          return;
        }
        hydrateCatalogueItem(catalogue.id, resolved.item);
        setHydrationFailure(null);
      })
      .catch(() => {
        if (!cancelled) {
          setHydrationFailure({
            itemId: editingItemId,
            reason: "fetch_failed",
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [
    adapter,
    catalogue.id,
    editingItemId,
    hydratedItemIds,
    newItemIds,
    modifiedItemIds,
    retryNonce,
  ]);

  const managedInCode = record.ownedBy === "code";
  // Nothing in the item editor saves on the full range; the catalogue's routes card saves on its own.
  const anythingToSave =
    editability.canAddItems || editability.editableItemFields.length > 0;
  const subtitle =
    catalogue.audience === "full_range"
      ? "The full range: staff only, mirroring the dictionary. Change the dictionary item to change one of these."
      : managedInCode
        ? // PROVISIONAL wording (docs/ui-wording.md), pending Ben's ruling on what stays editable.
          "The tech team changes this catalogue's items and the copy customers read. You can change the VAT treatment, the pricing group, the customer's product code and the note."
        : catalogue.audience === "core_range"
          ? "The default catalogue, shown to every customer with nothing allocated."
          : catalogue.privateTo
            ? `Private to ${catalogue.privateTo.customerName}.`
            : "Shared: shown to the customers it is allocated to.";

  // PROVISIONAL wording (catalogue curation tracker decision 14): the catalogue's own sheet, no customer wording.
  const downloadWorkbook = async () => {
    try {
      const { blob, filename } = await adapter.downloadDictionaryWorkbook({
        catalogueIds: [catalogue.id],
        wording: "none",
      });
      capabilities.downloadBlob(filename, blob);
      toast.success(`Downloaded ${filename}`);
    } catch {
      toast.error("The workbook could not be downloaded. Try again.");
    }
  };

  // Reload live items from the API and reset the draft to match server state.
  const reconcileFromServer = async () => {
    const fresh = await adapter.getCatalogueWithItems(catalogue.id);
    const freshItems = fresh?.items ?? [];
    onLiveItemsChange(freshItems);
    revertCatalogueDraft(catalogue.id, freshItems);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveFailure(null);
    // New items first (each derives, with its narrowing, from an existing
    // catalogue item; dictionary items are code, spec 03 §3.1), then modified
    // existing items. A saved item's spec (fields/options/quantity breaks)
    // belongs to the dictionary it derives from — this editor no longer writes
    // it: every item commits only presentation + narrowing (design doc
    // invariant 2: catalogue item = presentation + narrowing ONLY).
    const toSave = [...newItemIds, ...modifiedItemIds]
      .map((id) => items.find((i) => i.id === id))
      .filter((i): i is CatalogueItem => i !== undefined);
    // Items not yet persisted; the head is the one being saved.
    let pending = toSave;
    try {
      for (const item of toSave) {
        if (newItemIds.has(item.id)) {
          await adapter.deriveCatalogueItem(catalogue.id, {
            ...presentationInputFrom(item),
            sourceCatalogueItemId: item.derivesFromCatalogueItemId,
          });
        } else {
          await adapter.updateCatalogueItem(
            catalogue.id,
            item.id,
            presentationInputFrom(item),
          );
        }
        pending = pending.slice(1);
      }
      // Reload from API so the draft reflects real server IDs
      await reconcileFromServer();
      closeItem();
      toast.success("Saved — live now");
    } catch (err) {
      // The save loop is not atomic: some items may already be persisted
      // before the failure. Reconcile the draft with server state so those
      // committed items drop their new/modified flags and a retry does not
      // re-create duplicates. Stay on the current view so the user can retry
      // the items that did not make it.
      await reconcileFromServer().catch(() => {});
      const fieldErrors = extractFieldErrors(err);
      const failing = pending[0];
      if (fieldErrors.length > 0 && failing) {
        // The server refused what this person typed. Put the unsaved items
        // back into the working copy (the reconcile above dropped them) so
        // their text is still there to correct, and open the refused one.
        for (const item of pending) upsertCatalogueItem(catalogue.id, item);
        setSaveFailure({ itemId: failing.id, errors: fieldErrors });
        openItem(failing.id);
        toast.error(
          `${failing.name} was not saved: ${fieldErrors.length} ${fieldErrors.length === 1 ? "problem" : "problems"} to fix.`,
        );
      } else {
        toast.error(
          "Some changes may not have saved — reloaded from server; please review and retry.",
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const handleRevert = () => {
    revertCatalogueDraft(catalogue.id, liveItems);
    closeItem();
    toast.info("Reverted to the saved version");
  };

  const editingItem = editingItemId
    ? items.find((i) => i.id === editingItemId)
    : undefined;
  // The single predicate the editable panel's mount depends on. Nothing else
  // may gate it: not "no error", not a separate "status" flag that could go
  // stale relative to the ledger it was read from. This is strictly stronger
  // than the old "do not transition on failure" guarantee — a URL cannot be
  // refused — and, unlike that guarantee, it holds regardless of how the id
  // arrived (grid click, shared link, refresh).
  const editingItemReady =
    !!editingItemId &&
    !!editingItem &&
    hydrationFailure?.itemId !== editingItemId &&
    (hydratedItemIds.has(editingItemId) ||
      newItemIds.has(editingItemId) ||
      modifiedItemIds.has(editingItemId));

  return (
    <div className="space-y-6">
      {/* Opening an item changes only the query string, and the guard ignores
          same-pathname navigation, so the item URL needs no allowance here.
          The `/catalogue/` one is still required for the Import workbook hop. */}
      <UnsavedChangesGuard
        when={dirty}
        allow={(p) => p.startsWith("/catalogue/")}
      />
      <EditorHeader
        backTo={paths.settingsCatalogues()}
        backLabel="Catalogues"
        name={catalogue.name}
        subtitle={subtitle}
        versions={[]}
        actions={
          <>
            <Badge variant={managedInCode ? "outline" : "secondary"}>
              {record.ownership}
            </Badge>
            {record.commands.map((command) => (
              <Button
                key={command.name}
                variant="outline"
                size="sm"
                disabled={!command.enabled}
                title={command.reason ?? undefined}
                onClick={() =>
                  openCommand({
                    endpoint: {
                      command: index.commandOf(command.name),
                      href: command.href,
                      dryRunHref: command.dry_run_href,
                    },
                    context: catalogue.name,
                  })
                }
              >
                {command.label}
              </Button>
            ))}
          </>
        }
        dirty={dirty}
        saving={saving}
        {...(anythingToSave
          ? { onSave: handleSave, onRevert: handleRevert }
          : {})}
      />
      {commandShell}

      {!editingItemId && view.kind === "grid" && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => void downloadWorkbook()}
            >
              <Download className="size-4" /> Download workbook
            </Button>
            <Button variant="outline" size="sm" asChild>
              <Link to={paths.dictionaryWorkbook()}>
                <Upload className="size-4" /> Import workbook
              </Link>
            </Button>
          </div>
          {/* Rendering already-loaded items, not a client-side filter of a
              list endpoint — this catalogue's items are fetched whole. */}
          {itemsWithoutVatTreatment > 0 && (
            <p className="text-xs text-amber-700 dark:text-amber-400">
              {itemsWithoutVatTreatment} of {items.length} items in this
              catalogue have no VAT treatment.
            </p>
          )}
          {/* Only session-new items can be taken out: they exist nowhere but
              this working copy, so dropping them is the whole operation. A
              saved item has no removal path at all — the save loop below sends
              only new and modified items, so removing one from the draft
              persisted nothing and the post-save refetch resurrected it. See
              CatalogueItemGrid's `removableItemIds` for what replaces this
              once retire-an-item ships. */}
          <CatalogueRouteNarrowingCard
            catalogue={catalogue}
            disabled={!editability.canChangeRouteNarrowing}
          />
          <CatalogueItemGrid
            tiles={items.map((item) => ({ item }))}
            manage
            onEdit={openItem}
            onRemove={(id) => removeCatalogueItem(catalogue.id, id)}
            removableItemIds={newItemIds}
            {...(editability.canAddItems
              ? { onNew: () => setView({ kind: "new" }) }
              : {})}
          />
        </>
      )}

      {editingItemId && (
        <div className="space-y-4">
          {/* "Editing" only once editing is actually possible. The tile gives
              us the name before the config resolves, so the in-flight label
              can still be specific without overstating what the screen can
              currently do. */}
          <SubBar
            label={
              editingItemReady && editingItem
                ? `Editing item · ${editingItem.name}`
                : editingItem
                  ? `Loading item · ${editingItem.name}`
                  : "Loading item"
            }
            onBack={closeItem}
          />
          {editingItemReady && editingItem ? (
            <ItemEditPanel
              key={editingItem.id}
              catalogue={catalogue}
              editability={editability}
              item={editingItem}
              onChange={(it) => upsertCatalogueItem(catalogue.id, it)}
              relatedChoices={items
                .filter((i) => i.id !== editingItem.id && !newItemIds.has(i.id))
                .map((i) => ({ id: i.id, name: i.name }))}
              saveErrors={
                saveFailure?.itemId === editingItem.id ? saveFailure.errors : []
              }
            />
          ) : hydrationFailure?.itemId === editingItemId ? (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm">
              <p className="font-medium text-destructive">
                {hydrationFailureMessage(hydrationFailure.reason)}
              </p>
              {/* Say why nothing is at risk. The reason this panel exists at
                  all is that rendering the editor over unresolved config would
                  let a save persist an empty spec over the item's real one. */}
              <p className="mt-1 text-muted-foreground">
                Nothing on screen can overwrite it — the editor stays closed
                until the saved configuration loads.
              </p>
              <div className="mt-3 flex items-center gap-2">
                {hydrationFailure.reason === "fetch_failed" && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setRetryNonce((n) => n + 1)}
                  >
                    Try again
                  </Button>
                )}
                <Button variant="ghost" size="sm" onClick={closeItem}>
                  Back to items
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center py-16 text-muted-foreground">
              <Loader2 className="size-5 animate-spin" />
            </div>
          )}
        </div>
      )}

      {!editingItemId && view.kind === "new" && (
        <div className="space-y-4">
          <SubBar label="New item" onBack={() => setView({ kind: "grid" })} />
          <CatalogueItemCreator
            scope={catalogue.id}
            submitLabel="Add to catalogue"
            onCreate={(item) => {
              upsertCatalogueItem(catalogue.id, item);
              setView({ kind: "grid" });
              openItem(item.id);
            }}
            onCancel={() => setView({ kind: "grid" })}
          />
        </div>
      )}
    </div>
  );
}

function SubBar({ label, onBack }: { label: string; onBack: () => void }) {
  return (
    <div className="flex items-center gap-3 text-sm">
      <button
        onClick={onBack}
        className="inline-flex items-center gap-1 text-primary hover:underline"
      >
        <ArrowLeft className="size-4" /> Back to items
      </button>
      <span className="text-muted-foreground">·</span>
      <span className="font-medium">{label}</span>
    </div>
  );
}

/**
 * Which Tharstern blueprint this item is actually built from (R1).
 *
 * The editor previously showed nothing: `productTypeId` was never
 * rendered. An operator could not tell which product type an item creates in,
 * which is the first thing you need in order to notice you are looking at the
 * wrong blueprint.
 *
 * The item's own `productLabel` is deliberately NOT used here. It is a curated
 * local alias that has drifted from Tharstern on purpose — id 36 is
 * "Brochures/Books — Perfect Bound" locally and "Perfect Bound Book" in
 * Tharstern, id 63 is "Business Cards (cXML)" against "cXML- Duplex Business
 * Card". Rendering the local label to establish provenance would be circular:
 * it is exactly the value that could be wrong. So the canonical name comes from
 * the derived-schema endpoint, alongside the schema's derivation date and hash.
 *
 * Failure is shown, not swallowed. "Couldn't load" is information — it means
 * the blueprint this item depends on is unreachable, and silently rendering
 * nothing would leave the operator unable to tell that from "no product type".
 */
function ProductTypeProvenance({
  item,
  schema,
  failed,
}: {
  item: CatalogueItem;
  schema: API.DerivedSchemaResponse | null | undefined;
  failed: boolean;
}) {
  return (
    <div className="rounded-md border border-border bg-muted/30 px-3 py-2">
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-xs">
        <span className="font-medium uppercase tracking-wide text-muted-foreground">
          Product type
        </span>
        <span className="font-medium">
          {item.productTypeId == null ? (
            "No in-house route"
          ) : (
            <>
              {schema ? schema.product_type_name : `PT${item.productTypeId}`}
              <span className="ml-1.5 font-normal text-muted-foreground">
                · id {item.productTypeId}
              </span>
            </>
          )}
        </span>
        {schema && (
          <>
            <span className="text-muted-foreground">
              Schema derived {formatDateOrDash(schema.derived_at ?? null)}
            </span>
            <span
              className="font-mono text-muted-foreground"
              title={schema.schema_hash ?? undefined}
            >
              {/* Null, not "", means never derived — the model uses empty
                  string as that sentinel and the transformer normalises it. */}
              {schema.schema_hash
                ? `hash ${schema.schema_hash.slice(0, 8)}`
                : "never derived"}
            </span>
          </>
        )}
        {schema === null && (
          <span className="text-muted-foreground">
            No cached schema for this product type
          </span>
        )}
        {failed && (
          <span className="text-destructive">
            Couldn&apos;t load this product type&apos;s schema
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * "This spec is owned elsewhere" — shown above the NarrowingEditor for an
 * item that derives directly from a dictionary item. Its fields, options and
 * quantity ladder are the dictionary item's own; this page can only narrow
 * them (see `ItemEditPanel`), never redefine them. Costs
 * get their own pointer to the same page below (the "Variant costs" card) —
 * this one is about the definition, not the pricing.
 */
function DictionarySpecNotice({
  dictionaryItemId,
}: {
  dictionaryItemId: string;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-xs">
      <span className="text-muted-foreground">
        Fields, options and the quantity ladder are defined by the dictionary.
        This item can only narrow that definition — to change it, open it in the
        dictionary.
      </span>
      <Button variant="outline" size="sm" asChild>
        <Link to={paths.dictionaryItemAdmin({ dictionaryItemId })}>
          Open in dictionary
          <ArrowUpRight className="size-3.5" />
        </Link>
      </Button>
    </div>
  );
}

/** The parent space an item's derivation may narrow — just enough of either
 *  a resolved catalogue item or a dictionary item's detail to feed NarrowingEditor. */
type ParentSpace = {
  fields: CatalogueField[];
  quantityBreaks: QuantityBreak[];
};

// ── Fulfilment routes (third-party fulfilment phase 2, #87) ─────────────
// In-house plus every supplier route, and whether supplier pricing is shown
// (#90), from the dictionary admin's controls. Null while loading or when
// they cannot be read — the route editor then simply does not render.

function useRouteChoices(): { routes: RouteChoice[]; shown: boolean } | null {
  const adapter = useDataAdapter();
  const [choices, setChoices] = useState<{
    routes: RouteChoice[];
    shown: boolean;
  } | null>(null);
  useEffect(() => {
    let cancelled = false;
    adapter
      .getSupplierPricingControls()
      .then((controls) => {
        if (cancelled) return;
        setChoices({
          shown: controls.shown,
          routes: [
            { routeCode: IN_HOUSE_ROUTE, routeLabel: "In-house" },
            ...controls.suppliers.map((s) => ({
              routeCode: s.routeCode,
              routeLabel: s.routeLabel,
              note: s.suspended ? "suspended" : null,
            })),
          ],
        });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [adapter]);
  return choices;
}

/** Time's Speed ladder, for the Speed half of the narrowing editor; empty until read. */
function useSpeedLadder(): SpeedLadderRung[] {
  const adapter = useDataAdapter();
  const [ladder, setLadder] = useState<SpeedLadderRung[]>([]);
  useEffect(() => {
    let cancelled = false;
    adapter
      .listSpeeds()
      .then((speeds) => {
        if (!cancelled) setLadder(speeds);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [adapter]);
  return ladder;
}

function routeLabel(routes: RouteChoice[], code: string): string {
  return routes.find((r) => r.routeCode === code)?.routeLabel ?? code;
}

/**
 * What a route narrowing would mean for ordering, as the backend words it
 * (`getRouteNarrowingConsequences`). Re-asked whenever the narrowing being
 * edited changes; empty for "every route" without a round trip, and on a
 * failed read — the editor then simply shows no consequence lines.
 */
function useRouteNarrowingConsequences(
  routes: RouteNarrowing,
  target: { catalogueId: string } | { catalogueItemId: string },
): string[] {
  const adapter = useDataAdapter();
  const [lines, setLines] = useState<string[]>([]);
  const routesKey = JSON.stringify(routes);
  const targetKey = JSON.stringify(target);
  useEffect(() => {
    if (isEveryRoute(routes)) {
      setLines([]);
      return;
    }
    let cancelled = false;
    adapter
      .getRouteNarrowingConsequences({ routes, ...target })
      .then((next) => {
        if (!cancelled) setLines(next);
      })
      .catch(() => {
        if (!cancelled) setLines([]);
      });
    return () => {
      cancelled = true;
    };
    // Keyed on the serialised narrowing and target, not their identities.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adapter, routesKey, targetKey]);
  return lines;
}

/** The catalogue's own route narrowing, saved on its own — it is not part of
 *  the item draft the page's Save commits. */
function CatalogueRouteNarrowingCard({
  catalogue,
  disabled,
}: {
  catalogue: Catalogue;
  /** The editor may not change this catalogue's routes (the full range). */
  disabled: boolean;
}) {
  const adapter = useDataAdapter();
  const routeChoices = useRouteChoices();
  const speedLadder = useSpeedLadder();
  const [saved, setSaved] = useState<RouteNarrowing>(
    catalogue.routeNarrowing ?? {},
  );
  const [draft, setDraft] = useState<RouteNarrowing>(
    catalogue.routeNarrowing ?? {},
  );
  const [saving, setSaving] = useState(false);
  const consequences = useRouteNarrowingConsequences(draft, {
    catalogueId: catalogue.id,
  });
  if (!routeChoices) return null;
  const changed = JSON.stringify(draft) !== JSON.stringify(saved);
  const invalid =
    (!draft.pinnedRoute && draft.allowedRoutes?.length === 0) ||
    draft.allowedSpeeds?.length === 0;
  return (
    <Card>
      <CardContent className="space-y-3 pt-6">
        <fieldset disabled={disabled} className="m-0 min-w-0 border-0 p-0">
          <RouteNarrowingEditor
            scope="catalogue"
            routes={routeChoices.routes}
            narrowing={draft}
            onChange={setDraft}
            supplierPricingShown={routeChoices.shown}
            consequences={consequences}
            speeds={speedLadder}
          />
        </fieldset>
        {changed && (
          <div className="flex gap-2">
            <Button
              size="sm"
              disabled={saving || invalid}
              onClick={async () => {
                setSaving(true);
                try {
                  const updated = await adapter.updateCatalogueRouteNarrowing(
                    catalogue.id,
                    draft,
                  );
                  const next = updated.routeNarrowing ?? {};
                  setSaved(next);
                  setDraft(next);
                  toast.success("Fulfilment routes saved");
                } catch (err) {
                  // The backend names every refusal (an unknown route, items
                  // already narrower than the change allows).
                  const lines = extractErrorDetailLines(err);
                  toast.error(
                    lines.length
                      ? lines.join(" ")
                      : mutationErrorMessage(
                          err,
                          "Could not save the fulfilment routes",
                        ),
                  );
                } finally {
                  setSaving(false);
                }
              }}
            >
              Save fulfilment routes
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setDraft(saved)}>
              Undo
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** Whether `field` (a request-body path) is bound to an input on this panel (the customer product page card, or the
 *  customer's product code), so the summary banner need not repeat it. */
function isBoundField(field: string): boolean {
  return (
    field === "customer_product_ref" ||
    field === "description" ||
    field === "spec_list" ||
    field.startsWith("spec_list.") ||
    field === "related_item_ids" ||
    field.startsWith("related_item_ids.")
  );
}

/** Controls the catalogue's ownership leaves read-only are disabled as a group: the backend names which item
 *  fields One saves (`editableItemFields`), and the editor decides nothing itself. */
function EditableWhen({
  fields,
  editability,
  children,
}: {
  fields: string[];
  editability: CatalogueEditability;
  children: React.ReactNode;
}) {
  const editable = fields.every((f) =>
    editability.editableItemFields.includes(f),
  );
  return (
    <fieldset disabled={!editable} className="m-0 min-w-0 border-0 p-0">
      {children}
    </fieldset>
  );
}

function ItemEditPanel({
  catalogue,
  editability,
  item,
  onChange,
  relatedChoices,
  saveErrors,
}: {
  catalogue: Catalogue;
  editability: CatalogueEditability;
  item: CatalogueItem;
  onChange: (it: CatalogueItem) => void;
  /** Other saved items in this catalogue that may be offered as related items. */
  relatedChoices: { id: string; name: string }[];
  /** The refusals from the last save of this item, empty when there are none. */
  saveErrors: FieldError[];
}) {
  const adapter = useDataAdapter();
  const routeChoices = useRouteChoices();
  const speedLadder = useSpeedLadder();
  const itemRouteConsequences = useRouteNarrowingConsequences(
    item.narrowing?.routes ?? {},
    { catalogueItemId: item.id },
  );
  // Every catalogue item derives from something — another catalogue item
  // (narrows a standard/customer item) or a dictionary item directly (a
  // core range item). Either way it has no spec of its own to edit
  // (design doc invariant 2: catalogue item = presentation + narrowing ONLY):
  // it gets a NarrowingEditor over the parent's resolved space.
  // The resolved parent this item narrows.
  const [parent, setParent] = useState<ParentSpace | null | undefined>(
    undefined,
  );
  // The copy this item inherits when it writes none of its own: only a
  // catalogue-item parent hands any down (a dictionary item carries none).
  const [inheritedCopy, setInheritedCopy] = useState<InheritedCopy | null>(
    null,
  );
  const [priceGroups, setPriceGroups] = useState<PriceGroupSummary[]>([]);
  const [priceGroupsLoadError, setPriceGroupsLoadError] = useState(false);
  const { schema, failed: schemaFailed } = useEstRequestSchema(
    item.productTypeId,
  );

  const loadPriceGroups = useCallback(() => {
    setPriceGroupsLoadError(false);
    adapter
      .listPriceGroups()
      .then(setPriceGroups)
      .catch(() => setPriceGroupsLoadError(true));
  }, [adapter]);

  useEffect(() => {
    loadPriceGroups();
  }, [loadPriceGroups]);

  useEffect(() => {
    if (item.derivesFromCatalogueItemId) {
      adapter
        .getResolvedCatalogueItem(item.derivesFromCatalogueItemId)
        .then((result) => {
          setParent(result?.item ?? null);
          setInheritedCopy(inheritedCopyFrom(result?.item ?? null));
        });
      return;
    }
    if (item.derivesFromDictionaryItemId) {
      adapter
        .getDictionaryItemDetail(item.derivesFromDictionaryItemId)
        .then((detail) => setParent(detail ?? null));
      return;
    }
    setParent(null);
    setInheritedCopy(null);
  }, [
    adapter,
    item.derivesFromCatalogueItemId,
    item.derivesFromDictionaryItemId,
  ]);

  const setNarrowing = (next: Narrowing) => {
    onChange({ ...item, narrowing: next });
  };

  const nonDefaultPriceGroups = priceGroups.filter(
    (g) => g.scope !== "central",
  );

  const fieldMessages = fieldErrorMessages(saveErrors);
  const unboundErrors = saveErrors.filter((e) => !isBoundField(e.field));

  return (
    <div className="space-y-8">
      {saveErrors.length > 0 && (
        <div
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm"
        >
          <p className="font-medium text-destructive">
            {saveErrors.length}{" "}
            {saveErrors.length === 1 ? "problem" : "problems"} to fix before
            this item can be saved.
          </p>
          {unboundErrors.length > 0 && (
            <ul className="mt-2 list-disc pl-5 text-destructive">
              {unboundErrors.map((e, i) => (
                <li key={`${e.field}-${i}`}>{e.message}</li>
              ))}
            </ul>
          )}
        </div>
      )}
      <ProductTypeProvenance
        item={item}
        schema={schema}
        failed={schemaFailed}
      />
      {catalogue.privateTo && (
        <EditableWhen
          fields={["customer_product_ref"]}
          editability={editability}
        >
          <div className="space-y-2">
            <label
              htmlFor="customer-product-ref"
              className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
            >
              Customer&apos;s product code
            </label>
            <p className="text-xs text-muted-foreground">
              The code {catalogue.privateTo.customerName} uses for this product.
            </p>
            <Input
              id="customer-product-ref"
              className="max-w-xs"
              value={item.customerProductRef ?? ""}
              aria-invalid={
                fieldMessages.customer_product_ref ? true : undefined
              }
              aria-describedby={
                fieldMessages.customer_product_ref
                  ? "customer-product-ref-error"
                  : undefined
              }
              onChange={(e) =>
                onChange({
                  ...item,
                  customerProductRef: e.target.value || undefined,
                })
              }
            />
            {fieldMessages.customer_product_ref && (
              <p
                id="customer-product-ref-error"
                className="text-sm text-red-600"
              >
                {fieldMessages.customer_product_ref}
              </p>
            )}
          </div>
        </EditableWhen>
      )}
      {parent === undefined ? (
        <div className="flex items-center justify-center py-10 text-muted-foreground">
          <Loader2 className="size-5 animate-spin" />
        </div>
      ) : parent === null ? (
        <p className="text-sm text-destructive">
          Couldn&apos;t load the item this derives from — its spec is
          unavailable to narrow against.
        </p>
      ) : (
        <>
          {item.derivesFromDictionaryItemId && (
            <DictionarySpecNotice
              dictionaryItemId={item.derivesFromDictionaryItemId}
            />
          )}
          <EditableWhen fields={["narrowing"]} editability={editability}>
            <NarrowingEditor
              parentFields={parent.fields}
              parentQuantityBreaks={parent.quantityBreaks}
              narrowing={item.narrowing ?? {}}
              onChange={setNarrowing}
            />
            {routeChoices && (
              <RouteNarrowingEditor
                scope="item"
                routes={routeChoices.routes}
                narrowing={item.narrowing?.routes ?? {}}
                onChange={(routes) =>
                  setNarrowing({ ...(item.narrowing ?? {}), routes })
                }
                inherited={
                  catalogue.routeNarrowing
                    ? describeRouteNarrowing(catalogue.routeNarrowing, (code) =>
                        routeLabel(routeChoices.routes, code),
                      )
                    : null
                }
                supplierPricingShown={routeChoices.shown}
                consequences={itemRouteConsequences}
                speeds={speedLadder}
              />
            )}
          </EditableWhen>
        </>
      )}
      {item.kind === "repeatable" && (
        // A repeatable item holds its own standing artwork (brandhub-order-routing 13): supplied, confirmed and
        // its proofs approved here, saved at once (not part of the catalogue draft the header saves).
        <ItemArtworkPanel catalogueItemId={item.id} />
      )}
      {item.derivesFromDictionaryItemId && (
        // Variants and costs live at dictionary grain (design doc "the
        // dictionary rewire") — a narrowed item has none of its own. The
        // matrix itself moved to the dictionary admin surface (design doc
        // 48): no second matrix implementation survives here.
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Variant costs</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Costs live at dictionary grain now — inspect and refresh the full
              variant × quantity matrix, classified against the current
              definition, on the dictionary admin surface.
            </p>
            <Button variant="outline" size="sm" className="mt-3" asChild>
              <Link
                to={paths.dictionaryItemAdmin({
                  dictionaryItemId: item.derivesFromDictionaryItemId,
                })}
              >
                Open in dictionary
                <ArrowUpRight className="size-4" />
              </Link>
            </Button>
          </CardContent>
        </Card>
      )}
      <EditableWhen
        fields={["description", "spec_list", "related_item_ids"]}
        editability={editability}
      >
        <ItemMerchandisingEditor
          value={{
            description: item.ownDescription ?? "",
            specList: item.ownSpecList,
            relatedItemIds: item.relatedItemIds,
          }}
          onChange={(next) =>
            onChange({
              ...item,
              ownDescription:
                next.description === "" ? undefined : next.description,
              ownSpecList: next.specList,
              relatedItemIds: next.relatedItemIds,
            })
          }
          inherited={inheritedCopy}
          relatedChoices={relatedChoices}
          errors={fieldMessages}
        />
      </EditableWhen>
      {/* Item-level pricing group override */}
      <EditableWhen fields={["price_group_id"]} editability={editability}>
        <div className="space-y-2">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Pricing group override
          </p>
          <p className="text-xs text-muted-foreground">
            Leave as &ldquo;Customer default&rdquo; to use each customer&apos;s
            assigned group. Set an override to pin this item to a specific
            pricing group regardless of customer.
          </p>
          <select
            className="rounded-md border border-border bg-background px-3 py-1.5 text-sm"
            value={item.priceGroupId ?? ""}
            onChange={(e) => {
              const val = e.target.value || undefined;
              onChange({ ...item, priceGroupId: val });
            }}
          >
            <option value="">Customer default (Standard)</option>
            {nonDefaultPriceGroups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
          {priceGroupsLoadError && (
            <p className="text-xs text-destructive">
              Price groups could not be loaded — the override list may be
              incomplete.{" "}
              <button
                type="button"
                onClick={loadPriceGroups}
                className="underline"
              >
                Try again
              </button>
            </p>
          )}
        </div>
      </EditableWhen>
      {/* VAT treatment — a property of the product, not the customer (see
          docs/features/customer-estimate-page/02_enterprise_architecture_impact.md
          §1). "Not set" is a real, honest state: an unresolved item hides the
          VAT rows on the estimate page and blocks card payment rather than
          guessing standard rate. */}
      <EditableWhen fields={["vat_treatment"]} editability={editability}>
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <label
              htmlFor="vat-treatment-select"
              className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
            >
              VAT treatment
            </label>
            {item.vatTreatment == null && (
              <Badge variant="warning">VAT not set</Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            Leave as &ldquo;Not set&rdquo; until someone confirms this
            product&apos;s VAT rate — the estimate page hides VAT and refuses
            card payment until it is set.
          </p>
          <select
            id="vat-treatment-select"
            className="rounded-md border border-border bg-background px-3 py-1.5 text-sm"
            value={item.vatTreatment ?? ""}
            onChange={(e) => {
              const val = (e.target.value ||
                null) as CatalogueItem["vatTreatment"];
              onChange({ ...item, vatTreatment: val });
            }}
          >
            <option value="">Not set</option>
            <option value="standard">Standard rate</option>
            <option value="reduced">Reduced rate</option>
            <option value="zero_rated">Zero-rated</option>
          </select>
        </div>
      </EditableWhen>
      {/* Weight and packing: read from the item's physical profile, entered here only where nothing derived or
          supplied gives it (physical-properties 06). A new item has no server id to hang figures on yet. */}
      {!item.id.startsWith("new-") && (
        <ItemPhysicalFactsPanel itemId={item.id} />
      )}
    </div>
  );
}
